//////////////////
// IMPORTS
//////////////////

// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer, QueryRenderer, ReadyState } from "react-relay";

// one-on-one ap imports
import { IWebAppProps, IWebAppState } from "./OneOnOneAutopilot.d";
import { OneOnOneAutopilotView } from "./OneOnOneAutopilotView";
import {
  orgWithOneOnOnesFragment,
  canAutopilotQuery,
  oneOnOneAutopilotBaseQuery,
} from "./OneOnOneAutopilot.gql";

// utils & constants
import { getCurrentOrg } from "@util/org.util";
import { track, TrackingEvents, trackFSEvent, page, PageEvents } from "@util/analytics.util";
import { EXPERIMENT_FLAGS } from "@clockwise/client-commons/src/util/experiment";
import { isOnOneOnOneCoverage } from "@util/experiment.util";

// routing
import { withRouteQuery } from "@routing/routes.util";

// styles
import { styles } from "./OneOnOnAutopilot.styles";
import withStyles from "@material-ui/core/styles/withStyles";

// components
import { NoRouteView } from "@components/no-route-view";
import { LoaderClockWithMessage } from "@clockwise/web-commons/src/components/loader-clock-with-message";

//////////////////
// COMPONENT
//////////////////
export class OneOnOneAutopilotComponent extends React.Component<IWebAppProps, IWebAppState> {
  constructor(props: IWebAppProps) {
    super(props);

    this.state = {
      trackedNotExperimentUser: false,
    };
  }

  public componentDidMount() {
    trackFSEvent(EXPERIMENT_FLAGS.oneOnOneCoverage);
    page(PageEvents.ONE_ON_ONES_AUTOPILOT);
  }

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  public renderLoader = () => {
    return (
      <div style={{ height: "100vh", display: "flex" }}>
        <LoaderClockWithMessage key="loader" message="Loading your One-On-Ones" />
      </div>
    );
  };

  public render() {
    const { viewer } = this.props;
    const { trackedNotExperimentUser } = this.state;
    const org = getCurrentOrg(viewer);

    if (!org) {
      return this.renderLoader();
    }

    if (!isOnOneOnOneCoverage(org)) {
      if (!trackedNotExperimentUser) {
        track(TrackingEvents.ONE_ON_ONES_AUTOPILOT.NOT_MEMBER_EXPERIMENT, {
          orgId: org.id,
        });
        this.setState({ trackedNotExperimentUser: true });
      }
      return <NoRouteView />;
    }

    const oneOnOneEventsResponse =
      (org?.oneOnOnesForCalendar?.__typename === "EventList" && org.oneOnOnesForCalendar.list) ||
      [];

    return (
      <div className="cw-max-w-screen-lg cw-min-w-[320px] cw-h-screen">
        <React.Fragment>
          <QueryRenderer
            variables={{
              orgRelayId: org.id,
              userCalendarIds: [org.primaryOrgCalendar],
              externalEventIds: oneOnOneEventsResponse.map(
                (event) => event.eventKey.externalEventId,
              ),
            }}
            query={canAutopilotQuery}
            environment={this.props.relay.environment}
            render={(readyState: ReadyState) => {
              const rendererOrg = readyState.props && readyState.props.org && readyState.props.org;

              if (!rendererOrg) {
                return this.renderLoader();
              }

              const canAutopilotList =
                (rendererOrg &&
                  rendererOrg.canScheduleResponse &&
                  rendererOrg.canScheduleResponse.__typename === "CanScheduleResponseList" &&
                  rendererOrg.canScheduleResponse.list) ||
                [];
              const canAutopilotMap = canAutopilotList.reduce(
                (mapper: { [key: string]: boolean }, curr: ISchema.ICanScheduleResponse) => {
                  mapper[curr.externalEventId] = curr.canAutopilot;
                  return mapper;
                },
                {},
              );

              return (
                <OneOnOneAutopilotView
                  org={org}
                  relay={this.props.relay}
                  oneOnOnesForCalendar={oneOnOneEventsResponse}
                  canAutopilotMap={canAutopilotMap}
                />
              );
            }}
          />
        </React.Fragment>
      </div>
    );
  }
}

//////////////////
// STYLES
//////////////////

export const OneOnOneAutopilotComponentStyled = withStyles(styles)(OneOnOneAutopilotComponent);

///////////
// Relay //
///////////

export const WebAppOneOnOneAutopilot = createFragmentContainer(
  OneOnOneAutopilotComponentStyled,
  orgWithOneOnOnesFragment,
);

export const WebAppOneOnOneAutopilotPage = withRouteQuery(
  WebAppOneOnOneAutopilot,
  oneOnOneAutopilotBaseQuery,
);
